/**
 * Inventory Summary Model - Encapsulates inventory summary data and provides clean interface for UI components
 * This model handles data transformation, validation, and calculated properties
 */
class InventorySummaryModel {
  private _serverData: any;

  constructor(serverData: any = null) {
    this._serverData = serverData;
  }

  // ===== SERVER DATA PROPERTIES =====

  get total_inventory_value() {
    return this._serverData?.total_inventory_value ?? 0;
  }

  get total_inventory_retail_value() {
    return this._serverData?.total_inventory_retail_value ?? 0;
  }

  get total_items_count() {
    const count = this._serverData?.total_items_count ?? 0;
    return count.toLocaleString('en-US');
  }

  get in_stock_items() {
    return this._serverData?.in_stock_items ?? 0;
  }

  get low_stock_items() {
    return this._serverData?.low_stock_items ?? 0;
  }

  get out_of_stock_items() {
    return this._serverData?.out_of_stock_items ?? 0;
  }

  get in_stock_percentage() {
    return this._serverData?.in_stock_percentage ?? 0;
  }

  get low_stock_percentage() {
    return this._serverData?.low_stock_percentage ?? 0;
  }

  get out_of_stock_percentage() {
    return this._serverData?.out_of_stock_percentage ?? 0;
  }

  // ===== FORMATTED PROPERTIES =====

  get formattedTotalInventoryValue() {
    return this._formatCurrency(this.total_inventory_value);
  }

  get formattedTotalInventoryRetailValue() {
    return this._formatCurrency(this.total_inventory_retail_value);
  }

  get formattedTotalItemsCount() {
    return this.total_items_count;
  }

  // ===== CALCULATED PROPERTIES =====

  get hasStockData() {
    return (
      this.in_stock_items !== undefined &&
      this.low_stock_items !== undefined &&
      this.out_of_stock_items !== undefined &&
      this.in_stock_percentage !== undefined &&
      this.low_stock_percentage !== undefined &&
      this.out_of_stock_percentage !== undefined
    );
  }

  // ===== HELPER METHODS =====

  _formatCurrency(value: number | null | undefined): string {
    if (value === null || value === undefined) {
      return '-';
    }
    return `$${Number(value).toLocaleString('en-US')}`;
  }

  // ===== UTILITY METHODS =====

  // ===== STATIC METHODS =====

  static fromServerData(serverData: any) {
    return new InventorySummaryModel(serverData);
  }

  static createEmpty() {
    return new InventorySummaryModel();
  }
}

export default InventorySummaryModel;

